import { useEffect, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import { PERSONA_LABELS, type GuardianType } from "@/services/api/guardiansv2/types";
import {
  AVAILABLE_MODELS,
  getProviderForModel,
  MODEL_OPTIONS_BY_PROVIDER,
} from "@/constants/llmModels";
import { Button } from "@/components/ui/button/Button";

const PERSONAS: GuardianType[] = ["ace", "ivy", "nova"];
const DEFAULT_MODEL = "claude-sonnet-4-6";

const CATALOG_MODEL_IDS: Set<string> = new Set(AVAILABLE_MODELS.map((m) => m.value));

function modelLabel(modelId: string): string {
  return AVAILABLE_MODELS.find((m) => m.value === modelId)?.label ?? modelId;
}

/** Per-persona model overrides. */
export function PersonasPanel() {
  const { showSuccess, showError } = useNotification();
  const [models, setModels] = useState<Record<string, { provider: string; model: string }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    GuardiansV2API.getSettings()
      .then((s) => setModels(s.persona_models ?? {}))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await GuardiansV2API.patchSettings({ persona_models: models });
      showSuccess("Persona models saved");
    } catch {
      showError("Failed to save persona models");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="personas-panel">
      <p className="text-sm text-slate-400">
        LLM used for each guardian persona. Defaults to {modelLabel(DEFAULT_MODEL)} when unset.
      </p>
      {PERSONAS.map((p) => {
        const currentModel = models[p]?.model ?? DEFAULT_MODEL;
        const isOffCatalog = !CATALOG_MODEL_IDS.has(currentModel);
        return (
          <div key={p} className="rounded-lg bg-slate-800/50 p-3">
            <label className="block text-sm font-medium text-slate-200" htmlFor={`persona-model-${p}`}>
              {PERSONA_LABELS[p]}
            </label>
            <select
              id={`persona-model-${p}`}
              value={currentModel}
              onChange={(e) => {
                const model = e.target.value;
                setModels((prev) => ({
                  ...prev,
                  [p]: {
                    provider: getProviderForModel(model),
                    model,
                  },
                }));
              }}
              className="mt-2 w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-slate-100"
              data-testid={`persona-model-${p}`}
            >
              {isOffCatalog ? (
                <option value={currentModel}>{currentModel} (current)</option>
              ) : null}
              {MODEL_OPTIONS_BY_PROVIDER.map((group) => (
                <optgroup key={group.provider} label={group.provider}>
                  {group.models.map((model) => (
                    <option key={model} value={model}>
                      {modelLabel(model)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        );
      })}
      <Button onClick={() => void save()} disabled={saving} loading={saving}>
        Save
      </Button>
    </div>
  );
}
